import { useHomePageRevamp } from '../use-is-home-page';
import { MagazineSectionLegacy } from './MagazineSection.legacy';
import { useEffect, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { useCountryContext } from '@archipro-website/localisation';
import { NewsletterTypeEnum } from 'generated/graphql';
import { useUserLoggedIn } from '@modules/user';
import type { loader as homeLoader } from '~/routes/_app._index';
import { NEWSLETTER_STATUS_API_ROUTE } from '~/routes/remix-api.user.newsletter-status';
import { RECENT_MAGAZINES_API_ROUTE } from '~/routes/remix-api.recent-magazines';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { ComingSoonPill } from '../components/ComingSoonPill';
import { ScrollReveal } from '../components/ScrollReveal';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import { SubscribeField } from '../components/SubscribeField';
import { useFeature } from '~/modules/root';
import portfolioImg from '../images/magazine/portfolio.jpg';
import chairImg from '../images/magazine/chair.jpg';
import herringboneImg from '../images/magazine/herringbone.jpg';
import stoveImg from '../images/magazine/stove.jpg';
import commercialImg from '../images/magazine/commercial.jpg';
import buildImg from '../images/products/kitchen.jpg';
import { button } from '~/ui-rocco/button-system';
import { PublicationsShowcase } from '../components/PublicationsShowcase';
import { SplitHeading } from '../components/SplitHeading';
import { HOME_MOBILE_BLEED } from '../mobile-carousel-bleed';

/**
 * Magazine (Figma `1118:32065` header + `997:34870` cards, white section).
 *
 * Uses the shared `DiscoverHeader` ("(05 – Digital Magazines)", Popular
 * categories + Sign up CTA) above four tall coloured cards for the editorial
 * series (The Project Portfolio / Curated Collection / The Commercial Edit /
 * Build). The subscribe form now lives in the footer "(06 – Subscribe)".
 * Static promotional content; series imagery reuses homepage assets.
 */
/** The newsletter series, linked to the listing filtered by type (`?sort=`). */
const NEWSLETTER_CATEGORIES = [
    { label: 'Curated Collection', to: '/digital-magazines?sort=curated' },
    {
        label: 'The Commercial Edit',
        to: '/digital-magazines?sort=commercialedit',
    },
    {
        label: 'The Project Portfolio',
        to: '/digital-magazines?sort=projectportfolio',
    },
    { label: 'Build', to: '/digital-magazines?sort=build' },
];

interface MagazineCard {
    number: string;
    title: string;
    /** Magazine series this card links to (latest issue resolved server-side). */
    type: NewsletterTypeEnum;
    bg: string;
    titleColor: string;
    numberColor: string;
    /** Single image (cards 1, 3, 4). */
    image?: string;
    /** Three-image collage (card 2): two stacked left + one tall right. */
    collage?: { topLeft: string; bottomLeft: string; right: string };
    hasVideo?: boolean;
    /** Wider title column so a long title stays two lines (e.g. card 3). */
    wideTitle?: boolean;
}

const CARDS: MagazineCard[] = [
    {
        number: '01',
        title: 'The Project Portfolio',
        type: NewsletterTypeEnum.ProjectPortfolio,
        image: portfolioImg,
        bg: '#989485',
        titleColor: '#e5e5e5',
        numberColor: '#e5e5e5',
        hasVideo: true,
    },
    {
        number: '02',
        title: 'Curated Collection',
        type: NewsletterTypeEnum.CuratedCollection,
        collage: {
            topLeft: chairImg,
            bottomLeft: herringboneImg,
            right: stoveImg,
        },
        bg: '#000000',
        titleColor: '#b5b5b5',
        numberColor: '#d4d1cc',
    },
    {
        number: '03',
        title: 'The Commercial Edit',
        type: NewsletterTypeEnum.CommercialEdit,
        image: commercialImg,
        bg: '#d4d1cc',
        titleColor: '#343434',
        numberColor: '#1f1f21',
        wideTitle: true,
    },
    {
        number: '04',
        title: 'Build',
        type: NewsletterTypeEnum.Build,
        image: buildImg,
        bg: '#8e8580',
        titleColor: '#e5e5e5',
        numberColor: '#e5e5e5',
    },
];

/** Latest magazine issues shown to already-subscribed members (BC-4516 #9). */
interface RecentMagazine {
    id: number;
    title: string;
    imageSrc: string;
    link: string;
}

/**
 * Title + "View" CTA beneath a magazine tile. The pill matches the FeatureStrip
 * ("About") carousel CTA so the cards read consistently across the page.
 */
const MagazineTileCaption = ({ title }: { title: string }) => (
    <div className="flex flex-col items-start max-md:gap-[0.8021rem] md:gap-[1.04vw] lg:gap-[1.248rem]">
        <p className="m-0 line-clamp-2 font-sans leading-[1.3] text-[#4c4c4c] max-md:text-[0.9056rem] md:text-[1.0125vw] md:text-[#7E7C78] lg:text-[1.215rem]">
            {title}
        </p>
        <span className={button('primary', ['s', 'l'])}>View</span>
    </div>
);

const MagazineSectionRevamp = () => {
    const { homeCopy, magazineLinksByType, digitalPublications } =
        useLoaderData<typeof homeLoader>();

    // US has no magazines yet — show a Coming Soon pill above the subscribe
    // field and make the preview cards non-interactive (copy stays as a teaser).
    const isComingSoon = useCountryContext().byCountry({
        US: true,
        AU: false,
        NZ: false,
    });

    // Hide the subscribe prompt for members already subscribed. The status is
    // resolved against HubSpot, so it loads off the critical path via a fetcher
    // (logged-out visitors always see the prompt).
    const { isLoggedIn } = useUserLoggedIn();
    const subscriptionFetcher = useFetcher<{ subscribed: boolean }>();
    useEffect(() => {
        if (
            isLoggedIn &&
            subscriptionFetcher.state === 'idle' &&
            subscriptionFetcher.data === undefined
        ) {
            subscriptionFetcher.load(NEWSLETTER_STATUS_API_ROUTE);
        }
    }, [isLoggedIn, subscriptionFetcher]);
    const isSubscribed = subscriptionFetcher.data?.subscribed === true;
    const reloadSubscription = () =>
        subscriptionFetcher.load(NEWSLETTER_STATUS_API_ROUTE);

    // Refreshing the status after an in-session subscribe flips `isSubscribed`
    // to true, which would otherwise unmount the field and drop its thank-you
    // message. Remember the in-session subscribe so the field stays mounted.
    const canChoosePublications = useFeature('revamp_2026');
    const [justSubscribed, setJustSubscribed] = useState(false);
    const handleSubscribeSuccess = () => {
        setJustSubscribed(true);
        reloadSubscription();
    };

    // Logged-in members start unresolved (status loads off the critical path),
    // so hold the prompt until the fetch returns to avoid flashing it at members
    // who turn out to be subscribed. Logged-out visitors are resolved immediately.
    // Once they subscribe in-session we keep the prompt mounted so its thank-you
    // message persists (BC-4516 QA).
    const subscriptionResolved =
        !isLoggedIn || subscriptionFetcher.data !== undefined;
    const showSubscribePrompt =
        justSubscribed || (subscriptionResolved && !isSubscribed);

    // Subscribed members see the latest issues in place of the subscribe prompt;
    // only fetched once we know they're subscribed.
    const recentMagazinesFetcher = useFetcher<{
        magazines: RecentMagazine[];
    }>();
    useEffect(() => {
        if (
            isSubscribed &&
            recentMagazinesFetcher.state === 'idle' &&
            recentMagazinesFetcher.data === undefined
        ) {
            recentMagazinesFetcher.load(RECENT_MAGAZINES_API_ROUTE);
        }
    }, [isSubscribed, recentMagazinesFetcher]);
    const recentMagazines = recentMagazinesFetcher.data?.magazines ?? [];

    // Subscribed members get the real latest issues in the carousel (empty
    // while the request is in flight) instead of the promo cards; everyone else
    // keeps the promo set. Gating on `isSubscribed` — not on whether issues have
    // loaded — keeps both the subscribe prompt and the promos away from
    // subscribed members even mid-fetch or when the request returns nothing.
    const showSubscriberIssues = isSubscribed && !isComingSoon;

    // Desktop steps through the publications in the showcase, each with its
    // own Subscribe. Subscribed members see their latest issues instead, and
    // with no publications to show desktop keeps the prompt and carousel. A
    // subscribe made in the showcase leaves it in place under the reader.
    const showShowcase =
        digitalPublications.length > 0 &&
        (justSubscribed || !showSubscriberIssues);
    const phonesOnly = showShowcase ? 'md:hidden' : undefined;

    return (
        // Black on desktop: the projects directory's near-black, closing the
        // page before the footer.
        <section className="w-full overflow-hidden bg-white text-[#1f1f21] md:overflow-x-clip md:overflow-y-visible md:bg-[#141414] md:text-offwhite">
            <DiscoverHeader
                theme="light"
                darkOnDesktop
                eyebrow="(05 – Digital Magazines)"
                // The projects header's layout: the rule and eyebrow at half
                // width on the right, and the same space around the heading.
                className="md:gap-[5.8333vw] lg:gap-[7rem]"
                headingGapClassName="md:gap-[5.8333vw] lg:gap-[7rem]"
                heading={
                    <SplitHeading first="Magazine quality" second="content" />
                }
                categoriesLabel="Digital Newsletters"
                categories={NEWSLETTER_CATEGORIES}
                description={homeCopy.magazineDescription}
                actionSlot={
                    isComingSoon ? (
                        <>
                            <ComingSoonPill className="md:border-white md:text-offwhite" />
                            {/* Hide the prompt for already-subscribed members
                                even though US has no issues to swap in yet. */}
                            {showSubscribePrompt &&
                                (canChoosePublications ? (
                                    <SubscribePublicationsTrigger
                                        size="l"
                                        className={phonesOnly}
                                        onSubscribed={handleSubscribeSuccess}
                                    />
                                ) : (
                                    <SubscribeField
                                        className={phonesOnly}
                                        onSuccess={handleSubscribeSuccess}
                                    />
                                ))}
                        </>
                    ) : (
                        // Phones only while the showcase is up: each of its
                        // publications carries its own Subscribe.
                        <div
                            className={cn(
                                'flex flex-col max-md:gap-4',
                                phonesOnly,
                            )}
                        >
                            {/* Subscribed members never need the prompt,
                                regardless of whether their issues have loaded. */}
                            {showSubscribePrompt &&
                                (canChoosePublications ? (
                                    <SubscribePublicationsTrigger
                                        size="l"
                                        className={phonesOnly}
                                        onSubscribed={handleSubscribeSuccess}
                                    />
                                ) : (
                                    <SubscribeField
                                        className={phonesOnly}
                                        onSuccess={handleSubscribeSuccess}
                                    />
                                ))}
                        </div>
                    )
                }
            />

            {/* Desktop: the publications one at a time, a pinned full-screen
                panel stepping through each as the reader scrolls. */}
            {showShowcase && (
                <PublicationsShowcase
                    publications={digitalPublications}
                    onSubscribed={handleSubscribeSuccess}
                    className="max-md:hidden md:mt-[5rem]"
                />
            )}

            {/* Editorial series cards (desktop too when the showcase is not
                up) — full-bleed, centre-aligned so partial cards peek at both
                edges and the loop reads as endless */}
            <ScrollReveal
                delay={120}
                className={cn(
                    'max-md:px-6 max-md:py-6 md:px-[1.927vw] md:py-[8.33vw] lg:px-[max(2.3124rem,calc((100%_-_120rem)/2_+_2.3124rem))] lg:py-[9.996rem]',
                    phonesOnly,
                )}
            >
                <Carousel
                    autoPlay={{ delay: 4000 }}
                    options={{
                        align: 'start',
                        dragFree: true,
                        duration: 35,
                        containScroll: 'trimSnaps',
                        // Wrap around at either end. Embla falls back to
                        // non-loop automatically when slides don't fill the view.
                        loop: true,
                    }}
                >
                    {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                        breaks at the loop seam; centre align needs symmetry). */}
                    <CarouselContent
                        className="max-md:-mx-[0.3674rem]"
                        viewportClassName={HOME_MOBILE_BLEED}
                    >
                        {showSubscriberIssues
                            ? recentMagazines.map((magazine) => (
                                  <CarouselItem
                                      key={magazine.id}
                                      className="max-md:basis-[calc(51.45%+0.7348rem)] max-md:px-[0.3674rem] md:basis-[29.68vw] md:px-[1.04vw] lg:basis-[35.616rem] lg:px-[1.248rem]"
                                  >
                                      <RoccoLink
                                          to={magazine.link}
                                          className="group flex w-full flex-col max-md:gap-3 md:gap-[1.04vw] lg:gap-[1.248rem]"
                                      >
                                          <div
                                              className="bg-neutral-200 relative w-full overflow-hidden rounded-tile"
                                              style={{
                                                  aspectRatio: '530 / 906',
                                              }}
                                          >
                                              {magazine.imageSrc && (
                                                  <img
                                                      src={magazine.imageSrc}
                                                      alt={magazine.title}
                                                      className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                      loading="lazy"
                                                  />
                                              )}
                                          </div>
                                          <MagazineTileCaption
                                              title={magazine.title}
                                          />
                                      </RoccoLink>
                                  </CarouselItem>
                              ))
                            : // Rendered twice: four cards (~1.2 viewports) aren't
                              // enough content for embla to loop, so it silently
                              // disables looping without the duplicate set.
                              [...CARDS, ...CARDS].map((card, index) => (
                                  <CarouselItem
                                      key={`${card.number}-${index}`}
                                      className="max-md:basis-[calc(51.45%+0.7348rem)] max-md:px-[0.3674rem] md:basis-[29.68vw] md:px-[1.04vw] lg:basis-[35.616rem] lg:px-[1.248rem]"
                                  >
                                      <RoccoLink
                                          to={
                                              magazineLinksByType[card.type]
                                                  ?.link ?? '/digital-magazines'
                                          }
                                          className={cn(
                                              'group flex w-full flex-col max-md:gap-3 md:gap-[1.04vw] lg:gap-[1.248rem]',
                                              isComingSoon &&
                                                  'pointer-events-none',
                                          )}
                                      >
                                          <div
                                              className="relative flex w-full flex-col overflow-hidden rounded-tile"
                                              style={{
                                                  backgroundColor: card.bg,
                                                  aspectRatio: '530 / 906',
                                              }}
                                          >
                                              <span
                                                  className="absolute font-sans leading-none max-md:top-[0.875rem] max-md:left-[0.875rem] max-md:text-[0.8176rem] md:top-[0.77vw] md:left-[0.625vw] md:text-[1.7325vw] md:tracking-[-0.0345vw] lg:top-[0.924rem] lg:left-[0.75rem] lg:text-[2.079rem] lg:tracking-[-0.0414rem]"
                                                  style={{
                                                      color: card.numberColor,
                                                  }}
                                              >
                                                  {card.number}
                                              </span>
                                              <span
                                                  className={cn(
                                                      'absolute font-sans leading-none tracking-[-0.02em] max-md:top-[16.3%] max-md:left-[0.875rem] max-md:text-[1.0609rem] md:top-[7.7vw] md:left-[0.625vw] md:text-[2.3438vw] lg:top-[9.24rem] lg:left-[0.75rem] lg:text-[2.8125rem]',
                                                      card.wideTitle
                                                          ? 'md:w-[24.4vw] lg:w-[29.28rem]'
                                                          : 'md:w-[20vw] lg:w-[24rem]',
                                                  )}
                                                  style={{
                                                      color: card.titleColor,
                                                  }}
                                              >
                                                  {card.title}
                                              </span>
                                              <div className="absolute overflow-hidden max-md:inset-x-[2.3%] max-md:top-[32%] max-md:bottom-[2%] md:inset-x-[0.833vw] md:top-[15.1vw] md:bottom-[1vw] lg:inset-x-[0.9996rem] lg:top-[18.12rem] lg:bottom-[1.2rem]">
                                                  {card.collage ? (
                                                      <div className="grid size-full grid-cols-2 gap-[0.0673rem] md:gap-[0.26vw] lg:gap-[0.312rem]">
                                                          <div className="grid grid-rows-2 gap-[0.0673rem] md:gap-[0.26vw] lg:gap-[0.312rem]">
                                                              <div className="overflow-hidden">
                                                                  <img
                                                                      src={
                                                                          card
                                                                              .collage
                                                                              .topLeft
                                                                      }
                                                                      alt=""
                                                                      aria-hidden="true"
                                                                      className="size-full object-cover"
                                                                      loading="lazy"
                                                                  />
                                                              </div>
                                                              <div className="overflow-hidden">
                                                                  <img
                                                                      src={
                                                                          card
                                                                              .collage
                                                                              .bottomLeft
                                                                      }
                                                                      alt=""
                                                                      aria-hidden="true"
                                                                      className="size-full object-cover"
                                                                      loading="lazy"
                                                                  />
                                                              </div>
                                                          </div>
                                                          <div className="overflow-hidden">
                                                              <img
                                                                  src={
                                                                      card
                                                                          .collage
                                                                          .right
                                                                  }
                                                                  alt=""
                                                                  aria-hidden="true"
                                                                  className="size-full object-cover"
                                                                  loading="lazy"
                                                              />
                                                          </div>
                                                      </div>
                                                  ) : (
                                                      <>
                                                          <img
                                                              src={card.image}
                                                              alt={card.title}
                                                              className="absolute inset-0 size-full max-w-none object-cover"
                                                              loading="lazy"
                                                          />
                                                          {card.hasVideo && (
                                                              <span className="absolute top-1/2 left-1/2 flex aspect-square w-[40%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/80">
                                                                  <span className="ml-1 size-0 border-y-[0.1552rem] border-l-[0.2587rem] border-y-transparent border-l-white md:border-y-[0.6vw] md:border-l-[1vw] lg:border-y-[0.72rem] lg:border-l-[1.2rem]" />
                                                              </span>
                                                          )}
                                                      </>
                                                  )}
                                              </div>
                                          </div>
                                          {!isComingSoon && (
                                              <MagazineTileCaption
                                                  title={card.title}
                                              />
                                          )}
                                      </RoccoLink>
                                  </CarouselItem>
                              ))}
                    </CarouselContent>
                </Carousel>
            </ScrollReveal>
        </section>
    );
};

/**
 * The homepage's `revamp_2026` design of this section, or its previous one
 * while the flag is off.
 */
export const MagazineSection = () =>
    useHomePageRevamp() ? <MagazineSectionRevamp /> : <MagazineSectionLegacy />;
